// @ts-strict-ignore
import React, {
  useCallback, useContext, useMemo, useState,
} from 'react';
import type { Option } from '@usealloy/component-library';
import {
  Box, Button, ButtonVariant, Checkbox, Icon, IconName, Modal, MultiSelect, TextField, Tooltip,
} from '@usealloy/component-library';
import { Controller, useFormContext } from 'react-hook-form';

import {
  getDataTypeOptions, getDimensionOptions, LowRangeOption, HighRangeOption,
} from 'flow/service/actions/matrixModels';
import type { FormState } from 'flow/service/actions/matrixModels';

import {
  MatrixOptionsContainer,
  StyledLeftColumnContainer,
  StyledRightColumnContainer,
  StyledTextArea,
} from './styles';
import type { NodeEditorAutoSaveTabProps } from '../useNodeEditorForm/types';
import useNodeFormAutoSave from '../useNodeFormAutoSave';
import usePreventEmptyRegister from '../usePreventEmptyRegister';
import { UserContext } from '~/dashboard/context';
import { ValueRangeConfig } from '../ValueRangeConfig/ValueRangeConfig';
import type { OutputValueRange, ValueRangeConfigOptions } from 'flow/store/engine/types';

export default function MatrixOptionsTab({
  handleAutoSave,
}: NodeEditorAutoSaveTabProps) {
  const { featureFlags } = useContext(UserContext);
  const dimensionsOptions = useMemo(() => getDimensionOptions(), []);
  const dataTypeOptions = useMemo(() => getDataTypeOptions(), []);

  const [modalTypeOpen, setModalTypeOpen] = useState<'dataType' | 'dimensions' | 'valueRanges'>(null);
  const [tempSelectedOption, setTempSelectedOption] = useState<Option>(null);

  const {
    control,
    register,
    setValue,
    handleSubmit,
    watch,
  } = useFormContext<FormState>();

  const defineValueRanges = watch('defineValueRanges');
  const dataTypeOption = watch('dataTypeOption');
  const outputValueRange = watch('outputValueRange');
  const outputValueRangeOptions = watch('outputValueRangeOptions');

  const updateOutputValueRange = useCallback(
    (value: OutputValueRange[]) => { setValue('outputValueRange', value); },
    [setValue],
  );

  const updateOutputValueRangeOptions = useCallback(
    (value: ValueRangeConfigOptions) => { setValue('outputValueRangeOptions', value); },
    [setValue],
  );

  const preventEmptyRegister = usePreventEmptyRegister<FormState>();

  const modalButtonText = {
    valueRanges: 'Remove Value Ranges',
    dimensions: 'Change Dimensions',
    dataType: 'Change Data Type',
  };

  const modalBodyText = {
    valueRanges: 'Are you sure you want to remove "Define ranges for values"? Unchecking this box will clear out any range value configuration.',
    dimensions: 'Are you sure you want to change the dimensions of the Matrix? Some data will be lost.',
    dataType: 'Are you sure you want to change the data type of the Matrix? Changing data type will clear out Matrix cell values.',
  };

  const handleOptionsChange = (input: Option) => {
    if (['1', '2'].includes(input.value)) {
      setModalTypeOpen('dimensions');
    } else {
      setModalTypeOpen('dataType');
    }
    setTempSelectedOption(input);
  };

  const handleDefineValueRangesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    if (!checked && defineValueRanges) {
      // Unchecking - show confirmation modal
      setModalTypeOpen('valueRanges');
    } else {
      // Checking - allow immediately
      setValue('defineValueRanges', checked, { shouldDirty: true });
      handleSubmit(handleAutoSave)();
    }
  };

  const handleCancelOption = () => {
    setTempSelectedOption(null);
    setModalTypeOpen(null);
  };

  const handleConfirmOption = () => {
    if (modalTypeOpen === 'valueRanges') {
      // Reset value ranges
      setValue('defineValueRanges', false, { shouldDirty: true });
      setValue('outputValueRange', [], { shouldDirty: true });
      setValue('outputValueRangeOptions', {
        lowRange: LowRangeOption.bounded,
        highRange: HighRangeOption.bounded,
      }, { shouldDirty: true });
    } else {
      const fieldName = modalTypeOpen === 'dimensions'
        ? 'dimensionsOption' : 'dataTypeOption';
      setValue(fieldName, tempSelectedOption, { shouldDirty: true });
      if (fieldName === 'dataTypeOption' && tempSelectedOption.value === 'string') {
        setValue('defineValueRanges', false, { shouldDirty: true });
      }
    }
    setTempSelectedOption(null);
    setModalTypeOpen(null);
    handleSubmit(handleAutoSave)();
  };

  useNodeFormAutoSave<FormState>({
    fn: handleAutoSave,
    ignoreFields: ['dataTypeOption', 'dimensionsOption'],
  });

  return (
    <MatrixOptionsContainer>
      <StyledLeftColumnContainer>
        <TextField
          label="Matrix Name"
          type="text"
          {...preventEmptyRegister('name')}
        />
        <Controller
          name="dataTypeOption"
          control={control}
          render={({ field: { value } }) => (
            <MultiSelect
              isMulti={false}
              isSearchable
              label={(
                <Box alignItems="center" gap={1}>
                  Data Type
                  <Tooltip
                    content={(
                      <Box direction="column" justifyContent="center">
                        <p>Specify a data type for Matrix Model cells.</p>
                        <p>Note that the headers can be integers or strings,</p>
                        <p>regardless of data format selected.</p>
                      </Box>
                    )}
                    delayDuration={0}
                    side="right"
                  >
                    <Icon icon={IconName.Info} size="small" />
                  </Tooltip>
                </Box>
              )}
              onChange={handleOptionsChange}
              options={dataTypeOptions}
              placeholder="select"
              selectedOptions={value}
            />
          )}
        />
        <Controller
          name="dimensionsOption"
          control={control}
          render={({ field: { value } }) => (
            <MultiSelect
              isMulti={false}
              isSearchable
              label={(
                <Box alignItems="center" gap={1}>
                  Dimensions
                  <Tooltip
                    content={(
                      <Box direction="column" justifyContent="center">
                        <p>Choose between 1D vs 2D</p>
                      </Box>
                    )}
                    delayDuration={0}
                    side="right"
                  >
                    <Icon icon={IconName.Info} size="small" />
                  </Tooltip>
                </Box>
              )}
              onChange={handleOptionsChange}
              options={dimensionsOptions}
              placeholder="select"
              selectedOptions={value}
            />
          )}
        />
        {featureFlags.customerRiskAssessmentCra && (
          <StyledTextArea
            label="Description"
            rows={1}
            {...register('description')}
          />
        )}
      </StyledLeftColumnContainer>
      <StyledRightColumnContainer>
        {!featureFlags.customerRiskAssessmentCra && (
          <StyledTextArea
            label="Description"
            {...register('description')}
          />
        )}
        {featureFlags.customerRiskAssessmentCra && dataTypeOption?.value === 'number' && (
          <Controller
            control={control}
            name="defineValueRanges"
            data-dd-action-name="output-define-value-ranges-checkbox"
            render={({ field: { value } }) => (
              <Checkbox
                checked={value}
                onChange={handleDefineValueRangesChange}
                label="Define ranges for values"
              />
            )}
          />
        )}
        {featureFlags.customerRiskAssessmentCra && defineValueRanges && dataTypeOption?.value === 'number' && (
          <ValueRangeConfig
            outputValueRange={outputValueRange}
            outputValueRangeOptions={outputValueRangeOptions}
            updateOutputValueRange={updateOutputValueRange}
            updateOutputValueRangeOptions={updateOutputValueRangeOptions}
          />
        )}
      </StyledRightColumnContainer>
      <Modal
        isOpen={!!modalTypeOpen}
        header="Are you sure?"
        hasCloseIcon={false}
        size="small"
        secondaryAction={(
          <Button data-dd-action-name="matrix-options-modal-cancel" onClick={handleCancelOption}>Cancel</Button>
        )}
        primaryAction={(
          <Button
            onClick={handleConfirmOption}
            variant={ButtonVariant.primary}
          >
            {modalButtonText[modalTypeOpen]}
          </Button>
        )}
      >
        {modalBodyText[modalTypeOpen]}
      </Modal>
    </MatrixOptionsContainer>
  );
}
